import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
	HelpCircle,
	Plus,
	Edit3,
	Trash2,
	Search,
	Baby,
	Shield,
	Save,
	X,
	ChevronDown,
	ChevronUp,
} from "lucide-react";
import styles from './Admin.module.css';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/lib/supabase";

import { AdminFAQ as FAQ } from "@/types/faqs"
import { formatStringToDate } from "@/utils/helpers";

const faqTopics = {
	parent: ["booking", "communication", "payment", "safety", "general"],
	doula: ["booking", "communication", "payment", "safety", "general"],
};

export default function FAQManagement() {
	const [faqs, setFaqs] = useState<FAQ[]>([]);
	const [filteredFaqs, setFilteredFaqs] = useState<FAQ[]>([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [activeTab, setActiveTab] = useState("parent");
	const [selectedTopic, setSelectedTopic] = useState("all");
	const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
	const [showAddModal, setShowAddModal] = useState(false);
	const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
	const [expandedFaq, setExpandedFaq] = useState<string | null>(null);

	// New/Edit FAQ form state
	const [formData, setFormData] = useState({
		question: "",
		answer: "",
		topic: "",
		user_role: "parent" as "parent" | "doula",
		is_active: true,
	});

	useEffect(() => {
		loadFaqs();
	}, []);

	useEffect(() => {
		filterFaqs();
	}, [faqs, searchTerm, activeTab, selectedTopic]);

	const loadFaqs = async () => {
		try {
			setLoading(true);

			const { data: session } = await supabase.auth.getSession();
			if (!session?.session?.access_token) {
				throw new Error("No session found");
			}

			const response = await fetch(
				`${supabase.supabaseUrl}/functions/v1/get-all-faqs`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${session.session.access_token}`,
						"Content-Type": "application/json",
					},
				},
			);

			if (!response.ok) {
				const errorText = await response.text();
				throw new Error(`HTTP ${response.status}: ${errorText}`);
			}

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to fetch FAQs");
			}

			setFaqs(result.data || []);
		} catch (error) {
			setFaqs([]);
		} finally {
			setLoading(false);
		}
	};

	const filterFaqs = () => {
		let filtered = faqs;

		// Filter by user role based on active tab
		filtered = filtered.filter((faq) => faq.user_role === activeTab);

		// Filter by topic
		if (selectedTopic !== "all") {
			filtered = filtered.filter((faq) => faq.topic === selectedTopic);
		}

		// Filter by search term
		if (searchTerm) {
			filtered = filtered.filter(
				(faq) =>
					faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
					faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
					faq.topic.toLowerCase().includes(searchTerm.toLowerCase()),
			);
		}

		setFilteredFaqs(filtered);
	};

	const handleAddFaq = () => {
		setFormData({
			question: "",
			answer: "",
			topic: "",
			user_role: activeTab as "parent" | "doula",
			is_active: true,
		});
		setEditingFaq(null);
		setShowAddModal(true);
	};

	const handleEditFaq = (faq: FAQ) => {
		setFormData({
			question: faq.question,
			answer: faq.answer,
			topic: faq.topic,
			user_role: faq.user_role,
			is_active: faq.is_active,
		});
		setEditingFaq(faq);
		setShowAddModal(true);
	};

	const handleSaveFaq = async () => {
		try {
			if (
				!formData.question.trim() ||
				!formData.answer.trim() ||
				!formData.topic
			) {
				return;
			}

			const { data: session } = await supabase.auth.getSession();
			if (!session?.session?.access_token) {
				throw new Error("No session found");
			}

			const requestBody = editingFaq
				? {
					action: "update",
					faq_id: editingFaq.id,
					data: {
						question: formData.question,
						answer: formData.answer,
						topic: formData.topic,
						user_role: formData.user_role,
						is_active: formData.is_active,
					},
				}
				: {
					action: "create",
					data: {
						question: formData.question,
						answer: formData.answer,
						topic: formData.topic,
						user_role: formData.user_role,
						is_active: formData.is_active,
					},
				};

			const response = await fetch(
				`${supabase.supabaseUrl}/functions/v1/manage-faq`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${session.session.access_token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify(requestBody),
				},
			);

			if (!response.ok) {
				const errorText = await response.text();
				throw new Error(`HTTP ${response.status}: ${errorText}`);
			}

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to save FAQ");
			}

			// Reload all FAQs to ensure fresh data and proper ordering
			await loadFaqs();

			setShowAddModal(false);
			setEditingFaq(null);
		} catch (error) {
			// Handle error silently
		}
	};

	const handleDeleteFaq = async (faqId: string) => {
		try {
			const { data: session } = await supabase.auth.getSession();
			if (!session?.session?.access_token) {
				throw new Error("No session found");
			}

			const response = await fetch(
				`${supabase.supabaseUrl}/functions/v1/manage-faq`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${session.session.access_token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						action: "delete",
						faq_id: faqId,
					}),
				},
			);

			if (!response.ok) {
				const errorText = await response.text();
				throw new Error(`HTTP ${response.status}: ${errorText}`);
			}

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to delete FAQ");
			}

			// Reload all FAQs to ensure fresh data
			await loadFaqs();
			setDeleteConfirm(null);
		} catch (error) {
			// Handle error silently
		}
	};

	const toggleFaqActive = async (faqId: string, isActive: boolean) => {
		try {
			const { data: session } = await supabase.auth.getSession();
			if (!session?.session?.access_token) {
				throw new Error("No session found");
			}

			const response = await fetch(
				`${supabase.supabaseUrl}/functions/v1/manage-faq`,
				{
					method: "POST",
					headers: {
						Authorization: `Bearer ${session.session.access_token}`,
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						action: "update",
						faq_id: faqId,
						data: {
							is_active: isActive,
						},
					}),
				},
			);

			if (!response.ok) {
				const errorText = await response.text();
				throw new Error(`HTTP ${response.status}: ${errorText}`);
			}

			const result = await response.json();

			if (!result.success) {
				throw new Error(result.error || "Failed to update FAQ status");
			}

			// Reload all FAQs to ensure fresh data
			await loadFaqs();
		} catch (error) {
			// Handle error silently
		}
	};

	const getTopicCounts = () => {
		const topics = faqTopics[activeTab as keyof typeof faqTopics];
		return topics.reduce(
			(acc, topic) => {
				acc[topic] = faqs.filter(
					(f) => f.user_role === activeTab && f.topic === topic,
				).length;
				return acc;
			},
			{} as Record<string, number>,
		);
	};


	const topicCounts = getTopicCounts();

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-sage-50 to-sage-100 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sage-600 mx-auto mb-4"></div>
					<p className="text-gray-600 font-karla">Loading FAQs...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-sage-50 to-sage-100">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				{/* Tabs */}
				<Tabs
					defaultValue="parent"
					value={activeTab}
					onValueChange={setActiveTab}
					className="space-y-6"
				>
					<div className="flex justify-center">
						<TabsList className={`${styles.tabButtons} grid w-fit grid-cols-2 border border-newTheme-lightGrey h-auto`}>
							<TabsTrigger
								value="parent"
								className={`${styles.tabButton} flex items-center gap-2 font-karla data-[state=active]:bg-newTheme-lime text-black data-[state=inactive]:opacity-50 transition-all duration-300 rounded-md m-0.5`}
							>
								<Baby className="w-4 h-4" />
								Parent FAQs (
								{faqs.filter((f) => f.user_role === "parent").length})
							</TabsTrigger>
							<TabsTrigger
								value="doula"
								className={`${styles.tabButton} flex items-center gap-2 font-karla data-[state=active]:bg-newTheme-lime text-black data-[state=inactive]:opacity-50 transition-all duration-300 rounded-md m-0.5`}
							>
								<Shield className="w-4 h-4" />
								Doula FAQs ({faqs.filter((f) => f.user_role === "doula").length}
								)
							</TabsTrigger>
						</TabsList>
					</div>

					<TabsContent value={activeTab} className="space-y-6">
						{/* Controls */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.1 }}
							className="glass-card border-0 shadow-lg p-6 rounded-2xl"
						>
							<div className="flex flex-col lg:flex-row gap-4 mb-6">
								{/* Search */}
								<div className="relative flex-1">
									<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
									<Input
										type="text"
										placeholder="Search FAQs by question, answer, or topic..."
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										className="pl-10 h-12 text-lg bg-white border border-gray-200 font-karla"
									/>
								</div>

								{/* Topic Filter */}
								<div className="lg:w-64 lg:text-right">
									<select
										onChange={(e) => setSelectedTopic(e.target.value)}
										className="h-12 text-lg bg-white border border-gray-200 font-karla rounded-md p-2" aria-placeholder="Filter by topic"
									>
										<option value="all">All Topics</option>
										{faqTopics[activeTab as keyof typeof faqTopics].map((topic) => (
											<option key={topic} value={topic}>
												{topic} ({topicCounts[topic] || 0})
											</option>
										))}
									</select>
								</div>

								{/* Add FAQ Button */}
								<Button
									onClick={handleAddFaq}
									className="lg:w-auto bg-newTheme-lime text-black h-12 font-karla shadow-btn-shadow border border-newTheme-lightGrey"
								>
									<Plus className="h-4 w-4 mr-2" />
									Add FAQ
								</Button>
							</div>

							{/* Quick Stats */}
							<div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
								<div className="text-center">
									<div className="text-2xl font-bold text-sage-600">
										{filteredFaqs.length}
									</div>
									<div className="text-sm text-black font-karla">
										{selectedTopic === "all"
											? "Total FAQs"
											: `${selectedTopic} FAQs`}
									</div>
								</div>
								<div className="text-center">
									<div className="text-2xl font-bold text-newTheme-spruce">
										{filteredFaqs.filter((f) => f.is_active).length}
									</div>
									<div className="text-sm text-black font-karla">Active</div>
								</div>
								<div className="text-center">
									<div className="text-2xl font-bold text-newTheme-orange">
										{filteredFaqs.filter((f) => !f.is_active).length}
									</div>
									<div className="text-sm text-black font-karla">
										Inactive
									</div>
								</div>
								<div className="text-center">
									<div className="text-2xl font-bold text-newTheme-lime">
										{faqTopics[activeTab as keyof typeof faqTopics].length}
									</div>
									<div className="text-sm text-black font-karla">Topics</div>
								</div>
							</div>
						</motion.div>

						{/* FAQs List */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.2 }}
							className="space-y-4"
						>
							{filteredFaqs.length === 0 ? (
								<Card className="glass-card border-0 shadow-lg">
									<CardContent className="p-12 text-center">
										<HelpCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
										<h3 className="text-xl font-semibold text-gray-900 mb-2 font-karla">
											No FAQs found
										</h3>
										<p className="text-gray-600 font-karla mb-6">
											{searchTerm
												? `No FAQs match "${searchTerm}". Try adjusting your search terms.`
												: `No FAQs for ${selectedTopic === "all" ? activeTab + "s" : selectedTopic} yet.`}
										</p>
										<Button
											onClick={handleAddFaq}
											className="bg-primary-500 hover:bg-primary-600 text-white"
										>
											<Plus className="h-4 w-4 mr-2" />
											Add First FAQ
										</Button>
									</CardContent>
								</Card>
							) : (
								filteredFaqs.map((faq, index) => (
									<motion.div
										key={faq.id}
										initial={{ opacity: 0, y: 20 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.6, delay: 0.1 + index * 0.05 }}
									>
										<Card
											className={`glass-card border-0 shadow-lg hover:shadow-xl transition-all duration-300 ${!faq.is_active ? "opacity-60" : ""
												}`}
										>
											<CardHeader className="pb-4">
												<div className="flex items-start justify-between">
													<div className="flex-1 min-w-0">
														<div className="flex items-center gap-2 mb-2">
															<span className="text-xs bg-newTheme-cotton text-black px-2 py-1 rounded-full font-karla border border-newTheme-lightGrey">
																{faq.topic}
															</span>
															<span
																className={`text-xs px-2 py-1 border rounded-full text-black font-karla ${faq.is_active
																	? "bg-newTheme-lime border-newTheme-lime"
																	: "bg-newTheme-orange border-newTheme-orange"
																	}`}
															>
																{faq.is_active ? "Active" : "Inactive"}
															</span>
														</div>
														<CardTitle
															className="text-lg font-karla cursor-pointer"
															onClick={() =>
																setExpandedFaq(
																	expandedFaq === faq.id ? null : faq.id,
																)
															}
														>
															<div className="flex items-center justify-between">
																<span className="flex-1">{faq.question}</span>
																{expandedFaq === faq.id ? (
																	<ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
																) : (
																	<ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
																)}
															</div>
														</CardTitle>
													</div>
												</div>
											</CardHeader>

											{expandedFaq === faq.id && (
												<CardContent>
													<div className="space-y-4">
														<div className="prose prose-sm max-w-none">
															<p className="text-gray-700 font-karla leading-relaxed whitespace-pre-wrap">
																{faq.answer}
															</p>
														</div>

														<div className="text-xs text-gray-500 font-karla border-t pt-3">
															Created {formatStringToDate(faq.created_at)}
															{faq.updated_at !== faq.created_at && (
																<> • Updated {formatStringToDate(faq.updated_at)}</>
															)}
														</div>

														<div className="flex items-center justify-between pt-3 border-t">
															<div className="flex space-x-2">
																<Button
																	size="sm"
																	variant="outline"
																	onClick={() => handleEditFaq(faq)}
																	className="font-karla"
																>
																	<Edit3 className="h-3 w-3 mr-1" />
																	Edit
																</Button>
																<Button
																	size="sm"
																	variant={
																		faq.is_active ? "destructive" : "default"
																	}
																	onClick={() =>
																		toggleFaqActive(faq.id, !faq.is_active)
																	}
																	className="font-karla"
																>
																	{faq.is_active ? "Deactivate" : "Activate"}
																</Button>
															</div>

															<Button
																size="sm"
																variant="destructive"
																onClick={() => setDeleteConfirm(faq.id)}
																className="font-karla"
															>
																<Trash2 className="h-3 w-3 mr-1" />
																Delete
															</Button>
														</div>
													</div>
												</CardContent>
											)}
										</Card>
									</motion.div>
								))
							)}
						</motion.div>
					</TabsContent>
				</Tabs>

				{/* Add/Edit FAQ Modal */}
				<Dialog open={showAddModal} onOpenChange={setShowAddModal}>
					<DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white/95 backdrop-blur-sm border border-gray-200 shadow-xl">
						<DialogHeader>
							<DialogTitle className="font-karla">
								{editingFaq ? "Edit FAQ" : "Add New FAQ"}
							</DialogTitle>
							<DialogDescription className="font-karla">
								{editingFaq
									? "Update the FAQ information below."
									: `Create a new FAQ for ${activeTab}s.`}
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-4">
							{/* Topic */}
							<div>
								<label htmlFor="topic" className="text-sm font-medium text-gray-700 font-karla">
									Topic
								</label>
								<select
									id="topic"
									aria-placeholder="Select a topic"
									value={formData.topic}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, topic: e.target.value }))
									}
									className="ml-1 mt-1 font-karla bg-white border border-newTheme-lightGrey p-2 rounded-md"
								>
									<option value="">Select a topic</option>
									{faqTopics[formData.user_role].map((topic) => (
										<option key={topic} value={topic}>
											{topic}
										</option>
									))}
								</select>
							</div>

							{/* Question */}
							<div>
								<label className="text-sm font-medium text-gray-700 font-karla">
									Question
								</label>
								<Input
									value={formData.question}
									onChange={(e) =>
										setFormData((prev) => ({
											...prev,
											question: e.target.value,
										}))
									}
									placeholder="Enter the FAQ question..."
									className="mt-1 font-karla"
								/>
							</div>

							{/* Answer */}
							<div>
								<label className="text-sm font-medium text-gray-700 font-karla">
									Answer
								</label>
								<Textarea
									value={formData.answer}
									onChange={(e) =>
										setFormData((prev) => ({ ...prev, answer: e.target.value }))
									}
									placeholder="Enter the detailed answer..."
									rows={6}
									className="mt-1 font-karla"
								/>
							</div>

							{/* Status */}
							<div className="flex items-center space-x-2">
								<input
									type="checkbox"
									id="is_active"
									checked={formData.is_active}
									onChange={(e) =>
										setFormData((prev) => ({
											...prev,
											is_active: e.target.checked,
										}))
									}
									className="rounded"
								/>
								<label
									htmlFor="is_active"
									className="text-sm font-medium text-gray-700 font-karla"
								>
									Active (visible to users)
								</label>
							</div>
						</div>

						<DialogFooter>
							<Button
								variant="outline"
								onClick={() => setShowAddModal(false)}
								className="font-karla border border-newTheme-lightGrey shadow-btn-shadow"
							>
								<X className="h-4 w-4 mr-2" />
								Cancel
							</Button>
							<Button
								onClick={handleSaveFaq}
								disabled={
									!formData.question.trim() ||
									!formData.answer.trim() ||
									!formData.topic
								}
								className="bg-newTheme-lime text-black round-md shadow-btn-shadow border-newTheme-lightGrey font-karla border"
							>
								<Save className="h-4 w-4 mr-2" />
								{editingFaq ? "Update FAQ" : "Create FAQ"}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Delete Confirmation */}
				<AlertDialog
					open={!!deleteConfirm}
					onOpenChange={() => setDeleteConfirm(null)}
				>
					<AlertDialogContent className="bg-white/95 backdrop-blur-sm border border-gray-200 shadow-xl">
						<AlertDialogHeader>
							<AlertDialogTitle className="font-karla">
								Delete FAQ
							</AlertDialogTitle>
							<AlertDialogDescription className="font-karla">
								Are you sure you want to delete this FAQ? This action cannot be
								undone.
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel className="font-karla">
								Cancel
							</AlertDialogCancel>
							<AlertDialogAction
								onClick={() => deleteConfirm && handleDeleteFaq(deleteConfirm)}
								className="bg-red-600 hover:bg-red-700 font-karla"
							>
								Delete
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</div>
		</div>
	);
}
